import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import {
  AccountBalanceWalletOutlined,
  CheckCircleOutline,
  ErrorOutlineOutlined,
  PointOfSaleOutlined,
  ReceiptLong,
  Refresh as RefreshIcon,
  SearchOutlined,
} from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import DatePick from "../../Components/DatePick";
import MainText from "../../Components/MainText";
import { useAuth } from "../../context/AuthContext";
import { hasPermission } from "../../config/permissionUtils";
import { cashService, saleService } from "../../services";
import { emitCashSessionChanged, onCashSessionChanged } from "../../services/http/cashEvents";
import {
  CashSessionMovementsTable,
  ChannelSummaryCard,
  MetricCard,
} from "../Cash/components/CashSessionPanels";
import {
  buildEmptySessionDetail,
  calculateAdditionalMovementTotals,
  calculateClosureSummary,
  calculateSalesSessionMetrics,
  formatDateTime,
  getCashOfficeStatusColor,
  getCashOfficeStatusLabel,
  getMovementAmountColor,
  getMovementChannelLabel,
  getMovementReasonLabel,
  getMovementSignedAmount,
  getSessionStatusColor,
  getSessionStatusLabel,
  normalizeSessionDetail,
  toArray,
  toInputMoney,
  toMoney,
  toNumber,
  toSignedMoney,
} from "../Cash/cashSessionShared";

// ─── helpers ──────────────────────────────────────────────────────────────────

const todayDate = () => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

function toApiDate(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function methodIsActive(config, codes) {
  const list = toArray(config);
  return list.some(
    (item) =>
      codes.includes(String(item?.code || "").toUpperCase()) &&
      item?.is_active !== false
  );
}

function saleTypeLabel(sale) {
  return String(sale?.sale_type || "").toUpperCase() === "GROOMING"
    ? "Servicio grooming"
    : "Bien/producto";
}

function isSessionReceivable(session) {
  if (!session) return false;
  const status = String(session.status || "").toUpperCase();
  const officeStatus = String(session.cash_office_status || "").toUpperCase();
  return (
    status !== "OPEN" &&
    status !== "RECONCILED" &&
    officeStatus === "PENDING_RECEIPT" &&
    !session.received_at &&
    session.received_cash == null
  );
}

function isSessionReconciled(session) {
  if (!session) return false;
  const status = String(session.status || "").toUpperCase();
  const officeStatus = String(session.cash_office_status || "").toUpperCase();
  return (
    status === "RECONCILED" ||
    officeStatus === "BALANCED" ||
    officeStatus === "RECEIVED" ||
    Boolean(session.received_at) ||
    session.received_cash != null
  );
}

function saleNumberFromMovement(movement) {
  const text = String(movement?.description || "");
  const match = text.match(/(?:venta|sale|ticket)\s*#?\s*([A-Za-z0-9-]+)/i);
  return match?.[1] || "";
}

function findSaleForMovement(movement, sales) {
  if (!movement || movement.reason_type !== "SALE") return null;
  const movementSaleNumber = saleNumberFromMovement(movement);
  return toArray(sales).find(
    (sale) =>
      String(sale.id) === String(movementSaleNumber) ||
      String(sale.sale_number || "").toLowerCase() === String(movementSaleNumber).toLowerCase() ||
      String(movement.description || "").includes(String(sale.sale_number || "___never___"))
  ) || null;
}

function movementTypeLabel(movement, sales) {
  if (movement?.reason_type === "SALE") {
    return saleTypeLabel(findSaleForMovement(movement, sales));
  }
  return movement?.movement_type === "OUT" ? "Egreso" : "Ingreso";
}

const EMPTY_RECEIVE_FORM = {
  received_cash: "0.00",
  received_transfer: "0.00",
  received_voucher: "0.00",
  notes: "",
};

// ─── Summary Cards ────────────────────────────────────────────────────────────

function SummaryCard({ icon: Icon, label, value, helper, color = "primary" }) {
  const theme = useTheme();
  const palette = theme.palette[color] || theme.palette.primary;
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: alpha(theme.palette.divider, 0.55),
        boxShadow: "0 10px 30px rgba(15,23,42,0.045)",
      }}
    >
      <CardContent sx={{ p: 1.75, "&:last-child": { pb: 1.75 } }}>
        <Stack direction="row" spacing={1.3} alignItems="center">
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: 2,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(palette.main, 0.1),
              color: palette.main,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ textTransform: "uppercase", letterSpacing: "0.04em" }}
            >
              {label}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: 20, lineHeight: 1.1 }}>
              {value}
            </Typography>
            {helper ? (
              <Typography variant="caption" color="text.secondary">
                {helper}
              </Typography>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ─── Receive Dialog ───────────────────────────────────────────────────────────

function ReceiveDialog({
  open,
  onClose,
  onSubmit,
  submitting,
  session,
  summary,
  form,
  setForm,
  paymentConfig,
}) {
  const transferEnabled = methodIsActive(paymentConfig, ["TRANSFER"]);
  const voucherEnabled = methodIsActive(paymentConfig, ["CARD", "VOUCHER"]);

  const cashReference = summary?.counted_cash ?? summary?.expected_cash;
  const transferReference = summary?.counted_transfer ?? summary?.expected_transfer;
  const voucherReference = summary?.counted_card ?? summary?.expected_card;
  const cashDiff = toNumber(form.received_cash) - toNumber(cashReference);
  const transferDiff = transferEnabled
    ? toNumber(form.received_transfer) - toNumber(transferReference)
    : 0;
  const voucherDiff = voucherEnabled
    ? toNumber(form.received_voucher) - toNumber(voucherReference)
    : 0;
  const totalDiff = cashDiff + transferDiff + voucherDiff;

  const update = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        Recibir/Cuadrar {session?.id ? `#${session.id}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ pt: 0.5 }}>
          <Alert severity="info">
            El cuadre se guarda separado del detalle. Revisa los montos antes de
            confirmar.
          </Alert>

          <TextField
            label="Efectivo recibido"
            size="small"
            type="number"
            value={form.received_cash}
            onChange={(e) => update("received_cash", e.target.value)}
            inputProps={{ min: 0, step: "0.01" }}
            helperText={`Esperado: ${toMoney(summary?.expected_cash)} · Declarado por cajero: ${toMoney(cashReference)}`}
            fullWidth
          />

          {transferEnabled && (
            <TextField
              label="Transferencia recibida"
              size="small"
              type="number"
              value={form.received_transfer}
              onChange={(e) => update("received_transfer", e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              helperText={`Esperado: ${toMoney(summary?.expected_transfer)} · Declarado por cajero: ${toMoney(transferReference)}`}
              fullWidth
            />
          )}

          {voucherEnabled && (
            <TextField
              label="Voucher / tarjeta recibido"
              size="small"
              type="number"
              value={form.received_voucher}
              onChange={(e) => update("received_voucher", e.target.value)}
              inputProps={{ min: 0, step: "0.01" }}
              helperText={`Esperado: ${toMoney(summary?.expected_card)} · Declarado por cajero: ${toMoney(voucherReference)}`}
              fullWidth
            />
          )}

          <Card variant="outlined" sx={{ borderRadius: 1.5 }}>
            <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography sx={{ fontWeight: 700 }}>
                  Diferencia calculada
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 900,
                    color:
                      totalDiff === 0
                        ? "text.primary"
                        : totalDiff < 0
                        ? "error.main"
                        : "success.main",
                  }}
                >
                  {toSignedMoney(totalDiff)}
                </Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                Efectivo {toSignedMoney(cashDiff)} · Transferencia{" "}
                {toSignedMoney(transferDiff)} · Voucher {toSignedMoney(voucherDiff)}
              </Typography>
            </CardContent>
          </Card>

          <TextField
            label="Notas"
            size="small"
            multiline
            minRows={3}
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={onSubmit}
          disabled={submitting}
        >
          Guardar cuadre
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Detail Dialog ────────────────────────────────────────────────────────────

function MovementDetailDialog({ open, onClose, movement, sale }) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Detalle de movimiento</DialogTitle>
      <DialogContent dividers>
        {!movement ? (
          <Typography color="text.secondary">Selecciona un movimiento.</Typography>
        ) : (
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Grid container spacing={1.2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Fecha/hora</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{formatDateTime(movement.occurred_at || movement.created_at)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Tipo</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{getMovementReasonLabel(movement.reason_type)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Método/canal</Typography>
                    <Typography sx={{ fontWeight: 700 }}>{getMovementChannelLabel(movement.payment_channel)}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="caption" color="text.secondary">Monto</Typography>
                    <Typography sx={{ fontWeight: 900, color: getMovementAmountColor(movement) }}>
                      {toSignedMoney(getMovementSignedAmount(movement))}
                    </Typography>
                  </Grid>
                </Grid>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {movement.description || "Sin descripción"}
                </Typography>
              </CardContent>
            </Card>

            {sale ? (
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                  <Stack spacing={1.4}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                      <Box>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 900 }}>{sale.sale_number || `Venta #${sale.id}`}</Typography>
                          <Chip size="small" label={saleTypeLabel(sale)} variant="outlined" color={saleTypeLabel(sale) === "Servicio grooming" ? "info" : "default"} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">
                          Cliente: {sale.customer_display || "CF"} · Ticket: {sale.sale_number || sale.id} · {formatDateTime(sale.completed_at || sale.paid_at || sale.created_at)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Vendedor: {sale.created_by_name || sale.finalized_by_name || "-"} · Cobrador: {sale.cashier_name || sale.paid_by_name || sale.finalized_by_name || "-"}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontWeight: 950, fontSize: 18 }}>{toMoney(sale.total)}</Typography>
                    </Stack>

                    <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                      {toArray(sale.payments).map((payment) => (
                        <Chip
                          key={payment.id || `${payment.payment_method_name}-${payment.amount}`}
                          size="small"
                          variant="outlined"
                          label={`${payment.payment_method_name || payment.payment_method_code || "Pago"}: ${toMoney(payment.amount)}`}
                        />
                      ))}
                    </Stack>

                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Item</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Cantidad</TableCell>
                            <TableCell align="right">Unitario</TableCell>
                            <TableCell align="right">Total</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {toArray(sale.items).map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>{item.product_description || item.description_snapshot || item.service_name || "Item"}</TableCell>
                              <TableCell>{String(item.item_category || "").toUpperCase() === "GROOMING" ? "Servicio grooming" : "Bien/producto"}</TableCell>
                              <TableCell align="right">{item.qty_presentation || item.quantity || 0}</TableCell>
                              <TableCell align="right">{toMoney(item.unit_price)}</TableCell>
                              <TableCell align="right">{toMoney(item.line_total || item.total)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            ) : (
              <Alert severity="info">Movimiento sin venta vinculada. Si es egreso, revisa la razón y descripción del movimiento.</Alert>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ open, onClose, detail, loading }) {
  const session = detail?.session || null;
  const summary = detail?.summary || buildEmptySessionDetail().summary;
  const sales = toArray(detail?.sales);
  const movements = toArray(detail?.movements);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const selectedSale = findSaleForMovement(selectedMovement, sales);
  const movementTotals = useMemo(() => calculateAdditionalMovementTotals(movements), [movements]);
  const salesMetrics = useMemo(() => calculateSalesSessionMetrics(sales), [sales]);
  const closureSummary = useMemo(
    () => calculateClosureSummary({ summary, session, salesMetrics, movementTotals }),
    [movementTotals, salesMetrics, session, summary]
  );

  return (
    <>
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>
        Detalle de sesión {session?.id ? `#${session.id}` : ""}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Typography color="text.secondary">Cargando detalle...</Typography>
        ) : !session ? (
          <Typography color="text.secondary">
            Selecciona una sesión para consultar el detalle.
          </Typography>
        ) : (
          <Stack spacing={2}>
            <Grid container spacing={1.25}>
              <Grid item xs={12} md={2}>
                <MetricCard label="Ventas" value={String(salesMetrics.salesCount)} tooltip="Cantidad de ventas cobradas durante la sesion." />
              </Grid>
              <Grid item xs={12} md={2}>
                <MetricCard label="Productos vendidos" value={String(salesMetrics.productsSold)} tooltip="Cantidad total de unidades vendidas." />
              </Grid>
              <Grid item xs={12} md={2}>
                <MetricCard label="Ventas cobradas" value={toMoney(salesMetrics.collectedTotal)} tooltip="Total de ventas efectivamente pagadas durante la sesion." />
              </Grid>
              <Grid item xs={12} md={2}>
                <MetricCard label="Egresos" value={toMoney(movementTotals.outflow)} tooltip="Monto total de salidas registradas en la sesion." />
              </Grid>
              <Grid item xs={12} md={2}>
                <MetricCard label="Esperado al cierre" value={toMoney(closureSummary.expectedTotal)} tooltip="Apertura + ingresos + ventas - egresos." />
              </Grid>
              <Grid item xs={12} md={2}>
                <MetricCard label="Estado" value={closureSummary.status} tooltip="Cuadrado, con diferencia o pendiente segun el cierre y recepcion." />
              </Grid>
            </Grid>

            {/* Session info */}
            <Card variant="outlined" sx={{ borderRadius: 2 }}>
              <CardContent sx={{ p: 1.5 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Box>
                    <Typography sx={{ fontWeight: 700 }}>
                      {session?.cash_register?.name || "Caja"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Cajero: {session?.opened_by_name || "-"} · Sucursal:{" "}
                      {session?.cash_register?.branch?.name || "-"}
                    </Typography>
                  </Box>
                  <Stack
                    direction="row"
                    spacing={1}
                    flexWrap="wrap"
                    useFlexGap
                  >
                    <Chip
                      size="small"
                      label={`Apertura ${formatDateTime(session.opened_at)}`}
                    />
                    <Chip
                      size="small"
                      label={`Cierre ${formatDateTime(session.closed_at)}`}
                    />
                    <Chip
                      size="small"
                      label={getCashOfficeStatusLabel(
                        detail?.cash_office_status
                      )}
                      color={getCashOfficeStatusColor(
                        detail?.cash_office_status
                      )}
                      variant="outlined"
                    />
                  </Stack>
                </Stack>
              </CardContent>
            </Card>

            {/* Movements */}
            <Box>
              <Typography sx={{ fontWeight: 800, mb: 1 }}>
                Movimientos y egresos
              </Typography>
              <CashSessionMovementsTable
                movements={movements}
                variant="admin"
                canView
                onViewClick={setSelectedMovement}
                getMovementTypeLabel={(movement) => movementTypeLabel(movement, sales)}
                emptyMessage="No hay movimientos para esta sesión."
              />
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
    <MovementDetailDialog
      open={Boolean(selectedMovement)}
      onClose={() => setSelectedMovement(null)}
      movement={selectedMovement}
      sale={selectedSale}
    />
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function CashSessionAdminPage() {
  const theme = useTheme();
  const { currentBranch, isSuperuser, permissions = [] } = useAuth();
  const branchId = currentBranch?.id || null;

  const canViewPage =
    hasPermission(permissions, "cash_session_view", isSuperuser) ||
    hasPermission(permissions, "cash_session_manage", isSuperuser);
  const canManageSession = hasPermission(
    permissions,
    "cash_session_manage",
    isSuperuser
  );
  const canViewReports = hasPermission(permissions, "view_reports", isSuperuser);

  const today = useMemo(() => todayDate(), []);

  // ─── state ────────────────────────────────────────────────────────────────

  const [sessions, setSessions] = useState([]);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(buildEmptySessionDetail);
  const [sessionSales, setSessionSales] = useState([]);
  const [paymentConfig, setPaymentConfig] = useState([]);

  const [loadingSessions, setLoadingSessions] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [receiveSubmitting, setReceiveSubmitting] = useState(false);

  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [receiveOpen, setReceiveOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState(EMPTY_RECEIVE_FORM);
  const [movementDetail, setMovementDetail] = useState(null);

  const [filters, setFilters] = useState({
    date_from: today,
    date_to: today,
    status: "",
    cash_office_status: "",
    search: "",
  });

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnack = useCallback(
    (message, severity = "info") =>
      setSnack({ open: true, message, severity }),
    []
  );

  // ─── data fetching ─────────────────────────────────────────────────────────

  const loadSessions = useCallback(async () => {
    if (!branchId || !canViewPage) {
      setSessions([]);
      setSelectedSessionId(null);
      return;
    }

    setLoadingSessions(true);
    try {
      const params = {
        branch_id: branchId,
        date_from: toApiDate(filters.date_from) || undefined,
        date_to: toApiDate(filters.date_to) || undefined,
        status: filters.status || undefined,
        cash_office_status: filters.cash_office_status || undefined,
        search: filters.search || undefined,
      };
      const payload = await cashService.listSessions(params);
      const rows = toArray(payload);
      setSessions(rows);
    } catch {
      setSessions([]);
      setSelectedSessionId(null);
      showSnack("No se pudieron cargar las sesiones.", "error");
    } finally {
      setLoadingSessions(false);
    }
  }, [branchId, canViewPage, filters, showSnack]);

  const loadPaymentConfig = useCallback(async () => {
    if (!branchId || !canViewPage) return;
    try {
      const configData = await saleService.listPaymentMethodConfig({
        branch_id: branchId,
      });
      setPaymentConfig(toArray(configData));
    } catch {
      setPaymentConfig([]);
    }
  }, [branchId, canViewPage]);

  const loadSessionDetailAndSales = useCallback(
    async (sessionId) => {
      if (!branchId || !sessionId) {
        setSessionDetail(buildEmptySessionDetail());
        setSessionSales([]);
        return;
      }

      setLoadingDetail(true);
      let normalizedDetail = null;
      try {
        const detailPayload = await cashService.getSession(sessionId, {
          branch_id: branchId,
        });
        normalizedDetail = normalizeSessionDetail(detailPayload);
        setSessionDetail(normalizedDetail);
        setSessionSales(toArray(normalizedDetail?.sales));
      } catch {
        setSessionDetail(buildEmptySessionDetail());
        setSessionSales([]);
        showSnack(
          "No se pudo cargar el detalle de la sesión seleccionada.",
          "error"
        );
        setLoadingDetail(false);
        return;
      } finally {
        setLoadingDetail(false);
      }
    },
    [branchId, showSnack]
  );

  // ─── effects ──────────────────────────────────────────────────────────────

  useEffect(() => {
    loadPaymentConfig();
  }, [loadPaymentConfig]);

  useEffect(() => {
    loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (sessions.length === 0) {
      setSelectedSessionId(null);
      return;
    }
    setSelectedSessionId((prev) => {
      const exists = sessions.some((s) => Number(s.id) === Number(prev));
      return exists ? prev : sessions[0].id;
    });
  }, [sessions]);

  useEffect(() => {
    loadSessionDetailAndSales(selectedSessionId);
  }, [loadSessionDetailAndSales, selectedSessionId]);

  // Real-time updates
  useEffect(() => {
    const unsubscribe = onCashSessionChanged((payload) => {
      const payloadBranchId = payload?.branchId ?? null;
      if (
        !branchId ||
        (payloadBranchId && Number(payloadBranchId) !== Number(branchId))
      )
        return;
      loadSessions();
    });
    return unsubscribe;
  }, [branchId, loadSessions]);

  // ─── derived data ─────────────────────────────────────────────────────────

  const selectedSession = sessionDetail?.session || null;
  const selectedSessionForActions = selectedSession
    ? { ...selectedSession, cash_office_status: sessionDetail?.cash_office_status }
    : null;
  const selectedSummary =
    sessionDetail?.summary || buildEmptySessionDetail().summary;
  const selectedMovements = useMemo(
    () => toArray(sessionDetail?.movements),
    [sessionDetail?.movements]
  );
  const movementTotals = useMemo(
    () => calculateAdditionalMovementTotals(selectedMovements),
    [selectedMovements]
  );
  const movementDetailSale = findSaleForMovement(movementDetail, sessionSales);

  const salesMetrics = useMemo(() => calculateSalesSessionMetrics(sessionSales), [sessionSales]);
  const closureSummary = useMemo(
    () =>
      calculateClosureSummary({
        summary: selectedSummary,
        session: selectedSession,
        salesMetrics,
        movementTotals,
      }),
    [movementTotals, salesMetrics, selectedSession, selectedSummary]
  );

  // ─── aggregates for summary cards ─────────────────────────────────────────

  const aggregateSummary = useMemo(() => {
    const data = {
      pendingCount: 0,
      openCount: 0,
      pendingCash: 0,
      differenceCount: 0,
      differenceAmount: 0,
      openSinceYesterday: 0,
    };

    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    sessions.forEach((s) => {
      if (s.status === "OPEN") {
        data.openCount += 1;
        if (s.opened_at && now - new Date(s.opened_at).getTime() > oneDay) {
          data.openSinceYesterday += 1;
        }
      }
      if (s.cash_office_status === "PENDING_RECEIPT") {
        data.pendingCount += 1;
        data.pendingCash += Number(s?.summary?.expected_cash || 0);
      }
      if (
        s.received_difference_cash != null &&
        s.received_difference_cash !== 0
      ) {
        data.differenceCount += 1;
        data.differenceAmount += Number(s.received_difference_cash || 0);
      }
    });

    return data;
  }, [sessions]);

  // ─── actions ──────────────────────────────────────────────────────────────

  const refreshPage = useCallback(async () => {
    await loadSessions();
  }, [loadSessions]);

  const openReceive = useCallback(
    async (session) => {
      if (!session?.id || !isSessionReceivable(session)) {
        showSnack("Esta sesión ya fue recibida/cuadrada o no está lista para recibir.", "warning");
        return;
      }

      // Si el detalle actual ya es de esta sesión, usarlo directamente
      let detail = sessionDetail;
      if (detail?.session?.id !== session.id) {
        setLoadingDetail(true);
        try {
          detail = normalizeSessionDetail(
            await cashService.getSession(session.id, { branch_id: branchId })
          );
          setSessionDetail(detail);
        } catch (error) {
          showSnack(
            error?.response?.data?.detail || "No se pudo preparar el cuadre.",
            "error"
          );
          setLoadingDetail(false);
          return;
        } finally {
          setLoadingDetail(false);
        }
      }

      const summaryData = detail?.summary || session?.summary || {};
      setReceiveForm({
        received_cash: toInputMoney(
          session?.received_cash ?? summaryData.counted_cash ?? summaryData.expected_cash
        ),
        received_transfer: toInputMoney(
          session?.received_transfer ?? summaryData.counted_transfer ?? summaryData.expected_transfer
        ),
        received_voucher: toInputMoney(
          session?.received_voucher ?? summaryData.counted_card ?? summaryData.expected_card
        ),
        notes: session?.received_note || "",
      });
      setReceiveOpen(true);
    },
    [branchId, sessionDetail, showSnack]
  );

  const handleReceive = useCallback(async () => {
    const session = sessionDetail?.session;
    const sessionForReceive = session
      ? { ...session, cash_office_status: sessionDetail?.cash_office_status }
      : null;
    if (!branchId || !session?.id || !isSessionReceivable(sessionForReceive)) {
      showSnack("Esta sesión ya fue recibida/cuadrada o no está lista para recibir.", "warning");
      return;
    }

    const numericFields = [
      "received_cash",
      "received_transfer",
      "received_voucher",
    ];
    if (
      numericFields.some(
        (field) =>
          !Number.isFinite(Number(receiveForm[field])) ||
          Number(receiveForm[field]) < 0
      )
    ) {
      showSnack("Ingresa montos válidos para el cuadre.", "warning");
      return;
    }

    setReceiveSubmitting(true);
    try {
      const detail = normalizeSessionDetail(
        await cashService.receiveSession(session.id, {
          branch_id: branchId,
          received_cash: toNumber(receiveForm.received_cash).toFixed(2),
          received_transfer: toNumber(receiveForm.received_transfer).toFixed(2),
          received_voucher: toNumber(receiveForm.received_voucher).toFixed(2),
          notes: receiveForm.notes || "",
        })
      );
      setSessionDetail(detail);
      setReceiveOpen(false);
      emitCashSessionChanged({
        branchId,
        action: "cash_office_receive",
        sessionId: session.id,
      });
      await loadSessions();
      showSnack("Sesión recibida / cuadrada correctamente.", "success");
    } catch (error) {
      showSnack(
        error?.response?.data?.detail || "No se pudo guardar el cuadre.",
        "error"
      );
    } finally {
      setReceiveSubmitting(false);
    }
  }, [
    branchId,
    loadSessions,
    receiveForm,
    sessionDetail?.cash_office_status,
    sessionDetail?.session,
    showSnack,
  ]);

  // ─── guard clauses ────────────────────────────────────────────────────────

  if (!canViewPage) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sin permiso
        </Typography>
        <Typography variant="body2" color="text.secondary">
          No tienes permiso para ver cuadres de caja.
        </Typography>
      </Box>
    );
  }

  if (!branchId) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Sucursal no seleccionada
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Selecciona una sucursal para consultar sesiones de caja.
        </Typography>
      </Box>
    );
  }

  // ─── JSX ──────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%" }}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <MainText
          icon={ReceiptLong}
          title="Cuadres de caja"
          subtitle="Recepción administrativa de cortes por sesión"
        />
        <Tooltip title="Refrescar">
          <IconButton onClick={refreshPage}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Summary cards */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} lg={2.4}>
          <SummaryCard
            icon={PointOfSaleOutlined}
            label="Pendientes"
            value={String(aggregateSummary.pendingCount)}
            helper="Cortes por recibir"
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <SummaryCard
            icon={PointOfSaleOutlined}
            label="Abiertas"
            value={String(aggregateSummary.openCount)}
            helper="Sesiones activas"
            color="info"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <SummaryCard
            icon={AccountBalanceWalletOutlined}
            label="Efectivo pendiente"
            value={
              canViewReports
                ? toMoney(aggregateSummary.pendingCash)
                : "Restringido"
            }
            helper="Solo reportes"
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <SummaryCard
            icon={ErrorOutlineOutlined}
            label="Diferencias"
            value={
              canViewReports
                ? toSignedMoney(aggregateSummary.differenceAmount)
                : String(aggregateSummary.differenceCount)
            }
            helper={`${aggregateSummary.differenceCount} cortes`}
            color="error"
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={2.4}>
          <SummaryCard
            icon={PointOfSaleOutlined}
            label="Desde ayer"
            value={String(aggregateSummary.openSinceYesterday)}
            helper="Abiertas > 24h"
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          borderColor: alpha(theme.palette.divider, 0.6),
          mb: 2,
        }}
      >
        <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
          <Grid container spacing={1.25} alignItems="center">
            <Grid item xs={12} md={2}>
              <DatePick
                name="date_from"
                label="Desde"
                value={filters.date_from}
                onChange={(name, value) =>
                  setFilters((prev) => ({ ...prev, [name]: value }))
                }
                size="small"
                allowHistoricalDates
                maxWidth={220}
                minWidth={180}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <DatePick
                name="date_to"
                label="Hasta"
                value={filters.date_to}
                onChange={(name, value) =>
                  setFilters((prev) => ({ ...prev, [name]: value }))
                }
                size="small"
                allowHistoricalDates
                maxWidth={220}
                minWidth={180}
              />
            </Grid>
            <Grid item xs={12} md={1.7}>
              <TextField
                select
                label="Estado"
                size="small"
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value }))
                }
                fullWidth
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="OPEN">Abierta</MenuItem>
                <MenuItem value="CLOSED">Cerrada</MenuItem>
                <MenuItem value="RECONCILED">Conciliada</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField
                select
                label="Cuadre"
                size="small"
                value={filters.cash_office_status}
                onChange={(e) =>
                  setFilters((prev) => ({
                    ...prev,
                    cash_office_status: e.target.value,
                  }))
                }
                fullWidth
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="OPEN">Abierta</MenuItem>
                <MenuItem value="PENDING_RECEIPT">Pendiente recibir</MenuItem>
                <MenuItem value="BALANCED">Cuadrada</MenuItem>
                <MenuItem value="RECEIVED">Recibida con diferencia</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={4.3}>
              <TextField
                label="Buscar"
                size="small"
                value={filters.search}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, search: e.target.value }))
                }
                placeholder="Caja, cajero..."
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchOutlined fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Main content: session list + detail panel */}
      <Stack
        direction={{ xs: "column", lg: "row" }}
        spacing={2}
        alignItems="stretch"
      >
        {/* Session list */}
        <Box sx={{ width: { xs: "100%", lg: 360 }, flexShrink: 0 }}>
          <Card variant="outlined" sx={{ borderRadius: 2, height: "100%" }}>
            <CardContent sx={{ p: 1.5 }}>
              <Stack spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Sesiones
                </Typography>
                <Box
                  sx={{
                    maxHeight: { xs: "auto", lg: "calc(100vh - 420px)" },
                    overflowY: "auto",
                    pr: 0.5,
                  }}
                >
                  <Stack spacing={1}>
                    {loadingSessions ? (
                      <Typography variant="body2" color="text.secondary">
                        Cargando sesiones...
                      </Typography>
                    ) : sessions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No hay sesiones para el rango seleccionado.
                      </Typography>
                    ) : (
                      sessions.map((session) => {
                        const isSelected =
                          Number(session.id) === Number(selectedSessionId);
                        return (
                          <Paper
                            key={session.id}
                            variant="outlined"
                            onClick={() => setSelectedSessionId(session.id)}
                            sx={{
                              p: 1.25,
                              borderColor: isSelected
                                ? "primary.main"
                                : "divider",
                              bgcolor: isSelected
                                ? "rgba(16, 23, 60, 0.04)"
                                : "background.paper",
                              cursor: "pointer",
                              transition: "all 0.15s ease",
                              "&:hover": {
                                borderColor: "primary.light",
                                boxShadow:
                                  "0 10px 24px rgba(16, 23, 60, 0.06)",
                              },
                            }}
                          >
                            <Stack spacing={0.5}>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {session?.cash_register?.name ||
                                    session?.cash_register?.code ||
                                    `Caja ${session.cash_register_id}`}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getSessionStatusLabel(session?.status)}
                                  color={getSessionStatusColor(session?.status)}
                                  variant={
                                    String(
                                      session?.status || ""
                                    ).toUpperCase() === "OPEN"
                                      ? "filled"
                                      : "outlined"
                                  }
                                />
                              </Stack>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Cajero: {session?.opened_by_name || "-"}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Apertura: {formatDateTime(session?.opened_at)}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Cierre: {formatDateTime(session?.closed_at)}
                              </Typography>
                              <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Neto:{" "}
                                  {toSignedMoney(
                                    (session?.summary?.total_expected || 0) -
                                      (session?.summary?.total_outflow || 0)
                                  )}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={getCashOfficeStatusLabel(
                                    session?.cash_office_status
                                  )}
                                  color={getCashOfficeStatusColor(
                                    session?.cash_office_status
                                  )}
                                  variant="outlined"
                                />
                              </Stack>
                            </Stack>
                          </Paper>
                        );
                      })
                    )}
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Detail panel */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              {!selectedSessionId ? (
                <Typography variant="body2" color="text.secondary">
                  Selecciona una sesión para ver el detalle.
                </Typography>
              ) : loadingDetail ? (
                <Typography variant="body2" color="text.secondary">
                  Cargando detalle...
                </Typography>
              ) : selectedSession ? (
                <Stack spacing={2}>
                  {/* Header */}
                  <Box>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      flexWrap="wrap"
                      useFlexGap
                      sx={{ mb: 0.5 }}
                    >
                      <Typography variant="h6">
                        Sesión #{selectedSession.id}
                      </Typography>
                      <Chip
                        size="small"
                        label={getSessionStatusLabel(selectedSession.status)}
                        color={getSessionStatusColor(selectedSession.status)}
                        variant={
                          String(
                            selectedSession.status || ""
                          ).toUpperCase() === "OPEN"
                            ? "filled"
                            : "outlined"
                        }
                      />
                      <Chip
                        size="small"
                        label={getCashOfficeStatusLabel(
                          selectedSession.cash_office_status
                        )}
                        color={getCashOfficeStatusColor(
                          selectedSession.cash_office_status
                        )}
                        variant="outlined"
                      />
                    </Stack>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSession?.cash_register?.name || "Caja"} · Cajero:{" "}
                      {selectedSession?.opened_by_name || "-"} · Apertura{" "}
                      {formatDateTime(selectedSession.opened_at)}
                    </Typography>
                  </Box>

                  {/* Metrics row 1 */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    flexWrap="wrap"
                  >
                    <MetricCard
                      label="Ventas"
                      value={String(salesMetrics.salesCount)}
                      tooltip="Cantidad de ventas cobradas durante la sesion."
                    />
                    <MetricCard
                      label="Productos vendidos"
                      value={String(salesMetrics.productsSold)}
                      tooltip="Cantidad total de unidades vendidas."
                    />
                    <MetricCard
                      label="Ventas cobradas"
                      value={toMoney(salesMetrics.collectedTotal)}
                      tooltip="Total de ventas efectivamente pagadas durante la sesion."
                      icon={ReceiptLong}
                      accent="info.main"
                    />
                    <MetricCard
                      label="Egresos"
                      value={toMoney(movementTotals.outflow)}
                      tooltip="Monto total de salidas registradas en la sesion."
                      icon={ErrorOutlineOutlined}
                      accent="error.main"
                    />
                  </Stack>

                  {/* Metrics row 2 */}
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1.25}
                    flexWrap="wrap"
                  >
                    <MetricCard
                      label="Ingresos adicionales"
                      value={toMoney(movementTotals.inflow)}
                      tooltip="Movimientos positivos distintos de ventas."
                      icon={AccountBalanceWalletOutlined}
                      accent="success.main"
                    />
                    <MetricCard
                      label="Movimientos netos"
                      value={toSignedMoney(movementTotals.net)}
                      tooltip="Ingresos adicionales menos egresos."
                      icon={PointOfSaleOutlined}
                      accent="primary.main"
                    />
                    <MetricCard
                      label="Apertura"
                      value={toMoney(selectedSummary.opening_float)}
                      tooltip="Monto inicial con el que se abrio la sesion."
                    />
                  </Stack>

                  {/* Channel cards */}
                  <Stack
                    direction={{ xs: "column", xl: "row" }}
                    spacing={1.25}
                  >
                    <ChannelSummaryCard
                      title="Efectivo"
                      backgroundColor="#F4FCF8"
                      expected={selectedSummary.expected_cash}
                      counted={selectedSummary.counted_cash}
                      difference={selectedSummary.difference_cash}
                      isSessionOpen={
                        String(
                          selectedSession?.status || ""
                        ).toUpperCase() === "OPEN"
                      }
                    />
                    <ChannelSummaryCard
                      title="Tarjeta"
                      backgroundColor="#EFF6FD"
                      expected={selectedSummary.expected_card}
                      counted={selectedSummary.counted_card}
                      difference={selectedSummary.difference_card}
                      isSessionOpen={
                        String(
                          selectedSession?.status || ""
                        ).toUpperCase() === "OPEN"
                      }
                    />
                    <ChannelSummaryCard
                      title="Transferencia"
                      backgroundColor="#F4F3FE"
                      expected={selectedSummary.expected_transfer}
                      counted={selectedSummary.counted_transfer}
                      difference={selectedSummary.difference_transfer}
                      isSessionOpen={
                        String(
                          selectedSession?.status || ""
                        ).toUpperCase() === "OPEN"
                      }
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} flexWrap="wrap">
                    <MetricCard
                      label="Esperado al cierre"
                      value={toMoney(closureSummary.expectedTotal)}
                      tooltip="Apertura + ingresos + ventas - egresos."
                    />
                    <MetricCard
                      label="Contado cajero"
                      value={closureSummary.countedTotal == null ? "-" : toMoney(closureSummary.countedTotal)}
                      tooltip="Total declarado por el cajero al cerrar."
                    />
                    <MetricCard
                      label="Diferencia"
                      value={closureSummary.difference == null ? "-" : toSignedMoney(closureSummary.difference)}
                      tooltip="Contado cajero menos esperado al cierre."
                    />
                    <MetricCard
                      label="Recibido Cash Office"
                      value={closureSummary.receivedCashOffice == null ? "-" : toMoney(closureSummary.receivedCashOffice)}
                      tooltip="Monto recibido por Cash Office."
                    />
                    <MetricCard
                      label="Pendiente por recibir"
                      value={toMoney(closureSummary.pendingReceipt)}
                      tooltip="Monto contado o esperado que aun no ha sido recibido por Cash Office."
                    />
                    <MetricCard
                      label="Estado"
                      value={closureSummary.status}
                      tooltip="Cuadrado, con diferencia o pendiente segun el cierre y recepcion."
                    />
                  </Stack>

                  {/* Receive button */}
                  {canManageSession && (
                      <Box>
                        {isSessionReceivable(selectedSessionForActions) ? (
                          <Button
                            variant="contained"
                            color="success"
                            startIcon={<CheckCircleOutline />}
                            onClick={() => openReceive(selectedSessionForActions)}
                          >
                            Recibir/Cuadrar
                          </Button>
                        ) : isSessionReconciled(selectedSessionForActions) ? (
                          <Chip color="success" label="Conciliado" disabled />
                        ) : null}
                      </Box>
                    )}

                  {/* Movements */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mb: 1 }}
                    >
                      Movimientos
                    </Typography>
                    <CashSessionMovementsTable
                      movements={selectedMovements}
                      variant="admin"
                      canView
                      onViewClick={setMovementDetail}
                      getMovementTypeLabel={(movement) => movementTypeLabel(movement, sessionSales)}
                      emptyMessage="No hay movimientos para esta sesión."
                    />
                  </Box>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No se pudo resolver el detalle de la sesión seleccionada.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      </Stack>

      {/* Dialogs */}
      <DetailDialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        detail={sessionDetail}
        loading={loadingDetail}
      />
      <ReceiveDialog
        open={receiveOpen}
        onClose={() => setReceiveOpen(false)}
        onSubmit={handleReceive}
        submitting={receiveSubmitting}
        session={sessionDetail?.session}
        summary={sessionDetail?.summary}
        form={receiveForm}
        setForm={setReceiveForm}
        paymentConfig={paymentConfig}
      />
      <MovementDetailDialog
        open={Boolean(movementDetail)}
        onClose={() => setMovementDetail(null)}
        movement={movementDetail}
        sale={movementDetailSale}
      />

      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

